import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { User } from '../../../@core/services/session-manager.service';
import { UserApiActions } from '../../../@store/user/user.actions';
import {
  selectArchivedUsers,
  selectArchivedUsersError,
  selectArchivedUsersLoading,
  selectArchivedUsersTotalCount,
  selectIsAdmin,
} from '../../../@store/user/user.selectors';
import { UserService } from '../../../@store/user/user.service';
import { UserDetails } from '../../../@store/user/user.state.interface';
import { CONFIG } from '../../../config';
import { PaginationComponent } from '../../@shared/pagination/pagination.component';
import { UnarchivedUserDialogComponent } from '../unarchived-user-dialog/unarchived-user-dialog.component';

@Component({
  selector: 'ciwc-archived-users',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatSelectModule,
    PaginationComponent,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './archived-users.component.html',
  styleUrls: ['./archived-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArchivedUsersComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  readonly archivedUsers = this.store.selectSignal(selectArchivedUsers);
  readonly archivedUsersLength = this.store.selectSignal(selectArchivedUsersTotalCount);
  readonly usersLoading = this.store.selectSignal(selectArchivedUsersLoading);
  readonly usersError = this.store.selectSignal(selectArchivedUsersError);
  readonly isAdmin = this.store.selectSignal(selectIsAdmin);

  protected readonly Math = Math;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<User>;

  dataSource: MatTableDataSource<UserDetails> = new MatTableDataSource<UserDetails>();
  displayedColumns: string[] = ['name', 'email', 'actions'];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  totalUsers = 0;
  pageIndex = 0;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  currentSort: Sort = { active: 'name', direction: 'asc' };

  constructor() {
    effect(() => {
      this.initValues();
    });
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadUsers(): void {
    const payload = {
      status: 'INACTIVE',
      sort: 'firstName',
      offset: 1,
      limit: 10,
    };

    this.store.dispatch(UserApiActions.fetchArchivedUsers({ payload }));
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    this.store.dispatch(
      UserApiActions.fetchArchivedUsers({
        payload: {
          status: 'INACTIVE',
          sort: formattedSort,
          offset: this.pageIndex + 1,
          limit: this.pageSize,
        },
      }),
    );
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    const sortField = sort.active === 'name' ? 'firstName' : sort.active;
    const formattedSort = sort.direction === 'desc' ? `-${sortField}` : sortField;

    this.store.dispatch(
      UserApiActions.fetchArchivedUsers({
        payload: {
          status: 'INACTIVE',
          sort: formattedSort,
          offset: this.pageIndex + 1,
          limit: this.pageSize,
        },
      }),
    );
  }

  goToActiveUsers(): void {
    this.router.navigate(['/users/active']);
  }

  async openAddUserDialog(): Promise<void> {
    const dialogRef = this.dialog.open(MatDialog, {
      width: '400px',
      data: { roles: ['USER'] },
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.userService
        .addUser(result)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.loadUsers();
        });
    }
  }

  updatePageSize(size: number): void {
    if (this.paginator) {
      this.paginator.pageSize = size;
      this.pageSize = size;
    }
  }

  editUser(user: User): void {
    this.router.navigate(['/users/edit', user.id]);
  }

  viewUserDetails(user: User): void {
    this.router.navigate([`/users/${user.id}/view`]);
  }

  openUnarchiveDialog(user: User): void {
    const dialogRef = this.dialog.open(UnarchivedUserDialogComponent, {
      data: { user },
    });

    dialogRef.afterClosed();
  }

  initValues(): void {
    this.dataSource.data = this.archivedUsers()?.map((user: UserDetails) => ({
      id: user.id,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    }));

    this.totalUsers = this.archivedUsersLength();
  }
}
